/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, ember/require-tagless-components, warp-drive/no-legacy-request-patterns */
// Ember Imports
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import { computed } from '@ember/object';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
// Internal Imports
import {
  calculateYearLevelMilestoneDate,
  generateExpectationData,
  generateProgressData,
} from '@linc-technologies/linced-common/utils/progressions';
import ProgressionType from '@linc-technologies/linced-common/enums/progression-type';
import MilestoneType from '@linc-technologies/linced-common/enums/milestone-type';
import Options from '@linc-technologies/linced-common/enums/options';
import { byKeys, sortBy } from '@linc-technologies/linced-common/utils/arrays';
import { attributeBindings, classNames } from '@ember-decorators/component';

@classNames('report-builder__preview-pv')
@attributeBindings('containerStyle:style')
export default class ReportProgressVisual extends Component {
  // Services
  @service
  colours;

  @service
  dates;

  @service
  options;

  @service
  session;

  @service
  store;

  section = null;
  @tracked subject = null;
  @tracked theme = 'blue';
  templateChartType = 'line';
  @tracked mode = 'template';
  milestones = null;
  @tracked person = null;
  // The student's name as the report shows it (template student name formats);
  // falls back to person.fullName.
  personName = null;
  reportLabel = null;

  @computed(
    'allProgressions.@each.{judgement,levelId,createTime}',
    'milestones.[]',
    'person',
  )
  get _progressions() {
    let milestones = this.milestones || [];
    let person = this.person;
    if (!milestones.length || !person) {
      return [];
    }
    let personId = person.get('personId') || person.get('id');
    let milestoneIds = new Set(milestones.map((m) => m.get('id')));
    // OtjWidget always creates new progression records rather than updating
    // existing ones, so peekAll can accumulate multiple records for the same
    // milestone. Keep only the most recently created record per milestone so
    // the chart reflects the latest teacher judgement after a slider update.
    let latestByMilestone = new Map();
    this.allProgressions.forEach((p) => {
      if (
        p.get('person_id') === personId &&
        p.get('progressionType') === ProgressionType.PROGRESSION &&
        p.get('goal_subject_id') === this.subjectId &&
        milestoneIds.has(p.get('milestone.id')) &&
        // 0 (unspecified, the Go zero value) is no judgement.
        p.get('judgement') > 0
      ) {
        let milestoneId = p.get('milestone.id');
        let existing = latestByMilestone.get(milestoneId);
        let pTime = p.get('createTime');
        let eTime = existing && existing.get('createTime');
        // null createTime means the record was just created locally and the
        // server hasn't confirmed a timestamp yet — treat it as the newest.
        if (!existing || pTime == null || (eTime != null && pTime > eTime)) {
          latestByMilestone.set(milestoneId, p);
        }
      }
    });
    return Array.from(latestByMilestone.values());
  }

  themeColorMap = Object.freeze({
    teal: {
      containerBackground: 'rgba(220, 235, 235, 1)',
      columnBackgrounds: [
        '#9ccfd3',
        '#b7dde1',
        '#cfe8ea',
        '#e1eff0',
        '#edf5f5',
      ],
      headerFill: '#04667E',
      headerTextColor: '#ffffff',
    },
    blue: {
      containerBackground: 'rgba(218, 232, 236, 0.38)',
      columnBackgrounds: [
        '#04667cb3',
        '#78A9B4',
        '#8FB6BF',
        '#A6C4CA',
        '#BED1D6',
      ],
      headerFill: '#01667D',
      headerTextColor: '#ffffff',
    },
    warm: {
      containerBackground: 'rgba(254, 243, 199, 1)',
      columnBackgrounds: [
        '#fde68a',
        '#fef08a',
        '#fef9c3',
        '#fef5d3',
        '#fffbeb',
      ],
      headerFill: '#B45309',
      headerTextColor: '#ffffff',
    },
    monochrome: {
      containerBackground: '#F4F4F4',
      columnBackgrounds: [
        '#9ca3af',
        '#d1d5db',
        '#e5e7eb',
        '#f3f4f6',
        '#f9fafb',
      ],
      headerFill: '#374151',
      headerTextColor: '#ffffff',
    },
  });

  sampleGrades = Object.freeze([
    { value: 1, string: 'Beginning' },
    { value: 2, string: 'Developing' },
    { value: 3, string: 'Meeting' },
    { value: 4, string: 'Extending' },
    { value: 5, string: 'Exceeding' },
  ]);

  sampleYearLabels = Object.freeze([
    'Year 1',
    'Year 2',
    'Year 3',
    'Year 4',
    'Year 5',
    'Year 6',
  ]);

  sampleLevels = Object.freeze(
    Array.from({ length: 10 }, (_, i) => ({
      id: `sl${i}`,
      label: `Level ${i + 1}`,
    })),
  );

  @computed
  get sampleChartData() {
    return [
      {
        value: 's0',
        label: 'Year 0',
        data: { Below: 1.5, At: 2.5, Above: 3.5 },
      },
      {
        value: 's1',
        label: 'Year 1',
        data: { Below: 2.5, At: 3.5, Above: 4.5 },
      },
      {
        value: 's2',
        label: 'Year 2',
        data: { Below: 3.5, At: 4.5, Above: 5.5 },
      },
      {
        value: 's3',
        label: 'Year 3',
        data: { Below: 4.5, At: 5.5, Above: 6.5 },
      },
      {
        value: 's4',
        label: 'Year 4',
        data: { Below: 5.5, At: 6.5, Above: 7.5 },
      },
      {
        value: 's5',
        label: 'Year 5',
        data: { Below: 6.5, At: 7.5, Above: 8.5 },
      },
    ];
  }

  @computed
  get sampleLineData() {
    return [
      { tick: 's0', value: 2, label: 'Level 2' },
      { tick: 's1', value: 3, label: 'Level 3' },
      { tick: 's2', value: 4, label: 'Level 4' },
      { tick: 's3', value: 5, label: 'Level 5' },
      { tick: 's4', value: 6, label: 'Level 6' },
      { tick: 's5', value: 7, label: 'Level 7' },
    ];
  }

  @computed('theme')
  get themeColors() {
    return this.themeColorMap[this.theme] || this.themeColorMap.teal;
  }

  get containerStyle() {
    return htmlSafe(
      `background-color: ${this.themeColors.containerBackground}`,
    );
  }

  get headerBarStyle() {
    let { headerFill, headerTextColor } = this.themeColors;
    return htmlSafe(
      `background-color: ${headerFill}; color: ${headerTextColor}`,
    );
  }

  // Minimum pixel width at which the progress-table chart looks comfortable:
  // 72px for the year-label column + 90px per data column (wide-mode sizing).
  // On narrow screens the scroll wrapper enforces this so the SVG never shrinks
  // below a readable size — the user scrolls horizontally instead.
  @computed('gradeColumns.[]')
  get minChartWidth() {
    let nCols = (this.gradeColumns || []).length || 5;
    return 72 + nCols * 90;
  }

  @computed('minChartWidth')
  get minWidthStyle() {
    return htmlSafe(`min-width: ${this.minChartWidth}px`);
  }

  @computed('milestones.@each.yearLevel')
  get yearRangeLabel() {
    let milestones = this.milestones || [];
    let years = milestones
      .map((m) => m.get('yearLevel'))
      .filter((y) => y != null);
    if (!years.length) {
      return null;
    }
    let min = Math.min(...years);
    let max = Math.max(...years);
    return min === max ? `Year ${min}` : `Years ${min} to ${max}`;
  }

  // One column per possibleGrade, colours driven by the active theme.
  // Falls back to sample grades in template mode when no subject is linked.
  @computed('subject.possibleGrades.[]', 'themeColors', 'mode')
  get gradeColumns() {
    let grades = this.get('subject.possibleGrades') || [];
    if (!grades.length && this.mode === 'template') {
      grades = this.sampleGrades;
    }
    let { columnBackgrounds } = this.themeColors;
    return grades.map((grade, i) => {
      let bg = columnBackgrounds[i % columnBackgrounds.length];
      return {
        value: grade.value,
        label: grade.string,
        color: bg,
        headerStyle: htmlSafe(`background-color: ${bg}`),
        bodyStyle: htmlSafe(`background-color: ${bg}`),
      };
    });
  }

  // sampleRows are displayed when in "template" mode.
  @computed('gradeColumns', 'dates.currentAcademicYear')
  get sampleRows() {
    let cols = this.gradeColumns;
    let n = cols.length;
    if (!n) {
      return [];
    }

    let currentYear =
      this.dates.currentAcademicYear || new Date().getFullYear();
    let numRows = this.sampleYearLabels.length;
    // Month abbreviations for mid-year and end-year milestones
    const MONTHS = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    const primaryMonth = MONTHS[6]; // Jul — mid-year milestone
    const secondaryMonth = MONTHS[11]; // Dec — end-year milestone

    return this.sampleYearLabels.map((label, yearIndex) => {
      // Year 1 = furthest back, last row = current academic year
      let rowYear = currentYear - (numRows - 1 - yearIndex);
      let yy = String(rowYear).slice(-2);
      let primaryLabel = `${primaryMonth} ${yy}`;
      let secondaryLabel = `${secondaryMonth} ${yy}`;

      // Shift the pair one column right each year, capped so both chips fit.
      let startCol = n >= 2 ? Math.min(yearIndex, n - 2) : 0;

      let rawCells = cols.map((col, i) => {
        let badges = [];
        if (i === startCol) {
          badges = [{ label: primaryLabel, type: 'primary' }];
        } else if (n >= 2 && i === startCol + 1) {
          badges = [{ label: secondaryLabel, type: 'secondary' }];
        }
        return { badges, style: col.bodyStyle };
      });

      let cells = rawCells.map((cell, i) => {
        let nextCell = rawCells[i + 1];
        let prevCell = rawCells[i - 1];
        return {
          badges: cell.badges,
          style: cell.style,
          hasArrowRight:
            cell.badges.length > 0 && nextCell && nextCell.badges.length > 0,
          hasArrowLeft: false,
          isBridge: false,
          isBridgeLeft: false,
          isArrowTarget:
            cell.badges.length === 1 && prevCell && prevCell.badges.length > 0,
          isArrowTargetLeft: false,
        };
      });

      return { label, cells };
    });
  }

  // Real data: rows grouped by yearLevel, with each milestone in the group
  // placing a chip in the column matching progression.judgement (1-5 → grade value).
  @computed(
    'milestones.@each.sequence',
    '_progressions.@each.{milestoneId,judgement,levelId}',
    'gradeColumns',
    'person',
  )
  get reportRows() {
    let milestones = sortBy(this.milestones || [], 'sequence');
    let progressions = this._progressions || [];
    let cols = this.gradeColumns;
    let person = this.person;
    let rolloverMonthDate = this.dates.rolloverMonthDate;
    let currentYear = new Date().getFullYear();
    let n = cols.length;
    if (!n || !milestones.length) {
      return [];
    }

    // Group milestones by yearLevel (Year Level type) or label (Months/Weeks
    // at School types, which have no meaningful yearLevel, month, or day).
    let yearGroups = {};
    let yearOrder = [];
    milestones.forEach((milestone) => {
      let isYearLevel = milestone.get('type') === MilestoneType.YEAR_LEVEL;
      let key = isYearLevel
        ? String(milestone.get('yearLevel'))
        : milestone.get('label');
      if (!yearGroups[key]) {
        yearGroups[key] = {
          label: isYearLevel
            ? `Year ${milestone.get('yearLevel')}`
            : milestone.get('label'),
          milestones: [],
        };
        yearOrder.push(key);
      }
      yearGroups[key].milestones.push(milestone);
    });

    return yearOrder.map((key) => {
      let { label, milestones: yearMilestones } = yearGroups[key];

      // Build chip placement, tracking column index and level sequence.
      let chipsByCol = {};
      let chipSequence = []; // { colIndex, levelSequence } in milestone sequence order
      yearMilestones.forEach((milestone, mi) => {
        let progression = progressions.find(
          (p) => p.get('milestone.id') === milestone.get('id'),
        );
        if (!progression) {
          return;
        }
        let judgement = progression.get('judgement');
        let colIndex = cols.findIndex((col) => col.value === judgement);
        if (colIndex === -1) {
          return;
        }
        let level = progression.get('level');
        let levelSequence = level ? level.get('sequence') : null;
        let type = mi === 0 ? 'primary' : 'secondary';
        if (!chipsByCol[colIndex]) {
          chipsByCol[colIndex] = [];
        }
        let label;
        if (milestone.get('type') === MilestoneType.YEAR_LEVEL) {
          let year =
            currentYear -
            (person ? person.get('numericCurrentYearLevel') : 0) +
            milestone.get('yearLevel');
          let dueDate = calculateYearLevelMilestoneDate(
            year,
            milestone,
            rolloverMonthDate,
          );
          label = dueDate ? dueDate.format('MMM YY') : milestone.get('label');
        } else {
          label = milestone.get('label');
        }
        chipsByCol[colIndex].push({ label, type });
        chipSequence.push({ colIndex, levelSequence });
      });

      // Determine arrow direction between first and second chip.
      // When chips are in different columns, use column position.
      // When in the same column, fall back to level sequence ordering.
      let chipA = chipSequence[0];
      let chipB = chipSequence[1];
      let colA = chipA ? chipA.colIndex : undefined;
      let colB = chipB ? chipB.colIndex : undefined;
      let direction = null;
      let sameCellDirection = null;
      if (chipSequence.length === 2) {
        if (colA !== colB) {
          direction = colA < colB ? 'right' : 'left';
        } else {
          let seqA = chipA.levelSequence;
          let seqB = chipB.levelSequence;
          if (seqA !== null && seqB !== null && seqA !== seqB) {
            sameCellDirection = seqA < seqB ? 'right' : 'left';
          }
        }
      }

      let rawCells = cols.map((col, i) => ({
        badges: chipsByCol[i] || [],
        style: col.bodyStyle,
      }));

      let cells = rawCells.map((cell, i) => {
        return {
          badges: cell.badges,
          style: cell.style,
          sameCellDirection:
            cell.badges.length === 2 ? sameCellDirection : null,
          hasArrowRight: direction === 'right' && i === colA,
          hasArrowLeft: direction === 'left' && i === colA,
          isBridge: direction === 'right' && i > colA && i < colB,
          isBridgeLeft: direction === 'left' && i > colB && i < colA,
          isArrowTarget: direction === 'right' && i === colB,
          isArrowTargetLeft: direction === 'left' && i === colB,
        };
      });

      return { label, cells };
    });
  }

  @computed('mode', 'sampleRows', 'reportRows')
  get displayRows() {
    return this.mode === 'template' ? this.sampleRows : this.reportRows;
  }

  @computed('session.activeSchool')
  get progressChartColors() {
    return this.colours.charts(this.session.school);
  }

  @computed('subject')
  get subjectId() {
    return this.subject && this.subject.get('id');
  }

  get levels() {
    let subjectId = this.subjectId;
    if (!subjectId) {
      return [];
    }
    return this.store
      .peekAll('milestones/level')
      .filter((level) => level.get('goal_subject_id') === subjectId)
      .sort(byKeys('sequence', 'label'));
  }

  @computed('milestones.[]', 'levels.[]', 'subjectId', 'session.activeSchool')
  get expectationData() {
    let subjectId = this.subjectId;
    if (!subjectId) {
      return null;
    }
    let expectations = this.store
      .peekAll('milestones/expectation')
      .filter(
        (expectation) => expectation.get('goal_subject_id') === subjectId,
      );
    let showAllColours = this.options.schoolGet(
      this.session.school,
      Options.CHARTS_SHOW_ALL_COLOURS,
    );
    return generateExpectationData(
      expectations,
      this.milestones || [],
      this.levels,
      showAllColours,
    );
  }

  @computed(
    'milestones.[]',
    'levels.[]',
    '_progressions.@each.{milestoneId,judgement}',
  )
  get progressLineData() {
    return generateProgressData(
      this.milestones || [],
      this.levels,
      this._progressions || [],
    );
  }
}
